import { useQuery } from '@tanstack/react-query';
import { Card } from './Card';
import { api } from '../lib/api';

const WIDTH = 280;
const HEIGHT = 80;
const PAD_X = 4;
const PAD_Y = 8;

export function AccuracySparkline() {
  const q = useQuery({
    queryKey: ['profile', 'accuracy-trend'],
    queryFn: () => api.accuracyTrend(),
    retry: false, // 402 is the "non-premium" signal; don't retry
  });

  if (q.isLoading) {
    return (
      <Card>
        <div className="h-24 animate-pulse bg-bg-elevated rounded" />
      </Card>
    );
  }
  if (q.error || !q.data) return null;
  const weeks = q.data.weeks;
  if (weeks.length === 0) {
    return (
      <Card className="border-premium/40">
        <div className="font-display-alt text-xs uppercase tracking-widest text-premium mb-2">
          Accuracy trend · Premium
        </div>
        <div className="text-xs text-text-dim">
          No scored predictions in the last 12 weeks. Pick some matches and the trend will fill in.
        </div>
      </Card>
    );
  }

  // Build the polyline path
  const xs = weeks.map((_w, i) => PAD_X + (i / Math.max(1, weeks.length - 1)) * (WIDTH - 2 * PAD_X));
  const ys = weeks.map(
    (w) => HEIGHT - PAD_Y - (w.accuracyPct / 100) * (HEIGHT - 2 * PAD_Y),
  );
  const pathPoints = weeks
    .map((_w, i) => `${xs[i]?.toFixed(1)},${ys[i]?.toFixed(1)}`)
    .join(' ');

  const latest = weeks[weeks.length - 1];
  const first = weeks[0];
  const delta = latest && first ? latest.accuracyPct - first.accuracyPct : 0;

  return (
    <Card className="border-premium/40">
      <div className="flex items-baseline justify-between mb-2">
        <div className="font-display-alt text-xs uppercase tracking-widest text-premium">
          Accuracy trend · Premium
        </div>
        <div className="font-mono text-xs text-text-dim">
          {latest ? `${latest.accuracyPct}%` : '—'}{' '}
          {delta !== 0 && (
            <span className={delta > 0 ? 'text-pitch' : 'text-crimson'}>
              ({delta > 0 ? '+' : ''}
              {delta}pp)
            </span>
          )}
        </div>
      </div>
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="w-full h-20 block">
        {/* horizontal gridlines at 25/50/75 */}
        {[25, 50, 75].map((pct) => {
          const y = HEIGHT - PAD_Y - (pct / 100) * (HEIGHT - 2 * PAD_Y);
          return (
            <line
              key={pct}
              x1={PAD_X}
              x2={WIDTH - PAD_X}
              y1={y}
              y2={y}
              stroke="rgba(166,177,204,0.15)"
              strokeWidth={1}
            />
          );
        })}
        <polyline
          points={pathPoints}
          fill="none"
          stroke="#C8B6FF"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* dots */}
        {weeks.map((_w, i) => (
          <circle key={i} cx={xs[i]} cy={ys[i]} r={2.5} fill="#C8B6FF" />
        ))}
      </svg>
      <div className="font-display-alt text-[10px] uppercase tracking-widest text-text-faint mt-1 flex justify-between">
        <span>{first?.weekStart ? formatWeek(first.weekStart) : ''}</span>
        <span>this week</span>
      </div>
    </Card>
  );
}

function formatWeek(unix: number): string {
  const d = new Date(unix * 1000);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
